import { ChangeDetectionStrategy, Component, computed, input, output } from "@angular/core";
import { NgxEchartsDirective, provideEchartsCore } from "ngx-echarts";
import * as echarts from "echarts";
import type { ECElementEvent } from "echarts";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { buildReportChartOption, formatReportNumber, segmentFromChartClick } from "./report.chart.options";
import { ReportBreakdown, ReportChartType, ReportSegment, ReportSeries, ReportValueFormatter } from "./report.types";

@TypeManagerDecorator("s25-ng-report-chart")
@Component({
    selector: "s25-ng-report-chart",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgxEchartsDirective],
    providers: [provideEchartsCore({ echarts })],
    host: { class: "report-chart" },
    template: `
        <div
            class="report-chart-canvas"
            echarts
            role="img"
            [attr.aria-label]="ariaLabel()"
            [options]="option()"
            (chartClick)="onChartClick($event)"
        ></div>
    `,
    styles: `
        :host {
            display: block;
        }
        .report-chart-canvas {
            width: 100%;
            height: 360px;
        }
    `,
})
export class ReportChartComponent {
    readonly breakdown = input<ReportBreakdown | undefined>();
    readonly series = input<ReportSeries[]>([]);
    readonly measure = input.required<string>();
    readonly chartType = input.required<ReportChartType>();
    readonly title = input("");
    readonly formatValue = input<ReportValueFormatter>(formatReportNumber);

    readonly segmentClick = output<ReportSegment>();

    readonly option = computed(() =>
        buildReportChartOption({
            breakdown: this.breakdown(),
            series: this.series(),
            measure: this.measure(),
            chartType: this.chartType(),
            formatValue: this.formatValue(),
        }),
    );

    readonly ariaLabel = computed(() => {
        const rows = this.breakdown()?.rows ?? [];
        const title = this.title() || "Report chart";
        if (!rows.length) return `${title}: no data`;
        const topRows = rows
            .slice(0, 5)
            .map((row) => `${row.label} ${this.formatValue()(row.measures?.[this.measure()] ?? 0)}`)
            .join(", ");
        return `${title}: ${topRows}${rows.length > 5 ? ", and more" : ""}. Select a segment to see its entries.`;
    });

    onChartClick(event: ECElementEvent): void {
        const segment = segmentFromChartClick(event);
        if (segment) {
            this.segmentClick.emit(segment);
        }
    }
}
